import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";

import type { SignalTier } from "./SignalFeedSlice";
import { get } from "../../Services/ApiRequest";
import { API_URLS } from "../../Utils/Constants/APIConstants";

export type BhClass = "strong" | "stable" | "concern" | "no_data";
export type RlPriority = "high" | "medium" | "low" | "minimal" | "defer";
export type RlConditions = "open" | "cautious" | "blocked";
export type PortfolioRisk = "high" | "low" | "none";
export type ExpansionOpportunity = "strong" | "possible" | "none";

export interface PortfolioMapAccount {
  account_id: string;
  account_name: string;
  fit: number | null;
  bh: number | null;
  bh_class: BhClass;
  risk: number | null;
  opportunity: number | null;
  /** Public-data (xfactor) Risk / Opportunity — drives Net Investment × Fit. */
  public_risk: number | null;
  public_opportunity: number | null;
  net_investment_signal: number | null;
  ah: number | null;
  rl_priority: RlPriority | null;
  active_signal_count: number;
  top_signal_key: string | null;
  top_signal_tier: SignalTier | null;
  tier_counts: Partial<Record<SignalTier, number>>;
  // Phase 0 additions — mirrors mock's buildPublicDataSignals() Target AL /
  // RL Conditions / Portfolio Risk / Expansion Opportunity + BH sub-scores.
  target_al: number | null;
  rl_conditions: RlConditions | null;
  portfolio_risk: PortfolioRisk | null;
  expansion_opportunity: ExpansionOpportunity | null;
  bh_leadership: number | null;
  bh_growth: number | null;
  bh_financial: number | null;
  bh_legal: number | null;
}

interface PortfolioMapState {
  accounts: PortfolioMapAccount[];
  loading: boolean;
  error: string | null;
}

const initialState: PortfolioMapState = {
  accounts: [],
  loading: false,
  error: null,
};

export const fetchPortfolioMap = createAsyncThunk(
  "portfolioMap/fetch",
  async (_: void, { rejectWithValue }) => {
    try {
      const response = await get(API_URLS.getPortfolioMap());
      const content = (response as any)?.content || response;
      return (content?.accounts || []) as PortfolioMapAccount[];
    } catch (error: any) {
      return rejectWithValue(
        error?.response?.data?.message ||
          error?.message ||
          "Failed to fetch portfolio map"
      );
    }
  }
);

const portfolioMapSlice = createSlice({
  name: "portfolioMap",
  initialState,
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(fetchPortfolioMap.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchPortfolioMap.fulfilled, (state, action) => {
        state.loading = false;
        state.accounts = action.payload;
        state.error = null;
      })
      .addCase(fetchPortfolioMap.rejected, (state, action) => {
        state.loading = false;
        state.error =
          (action.payload as string) || action.error.message || "Unknown error";
      });
  },
});

export default portfolioMapSlice.reducer;
